import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, Volume2, VolumeX, MessageCircle } from 'lucide-react';
import { playChatChime } from '@/lib/chatChime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 10000;
const CHIME_KEY = 'rbx-chat-chime-enabled';
const TOAST_KEY = 'rbx-chat-toast-enabled';

const readBool = (key, fallback) => {
  const v = typeof window !== 'undefined' ? localStorage.getItem(key) : null;
  if (v === null) return fallback;
  return v === '1';
};

export const ChatNotificationBell = ({ onNavigateToChats }) => {
  const [count, setCount]           = useState(0);
  const [sessions, setSessions]     = useState([]);
  const [open, setOpen]             = useState(false);
  const [showPrefs, setShowPrefs]   = useState(false);
  const [chimeOn, setChimeOn]       = useState(() => readBool(CHIME_KEY, true));
  const [toastOn, setToastOn]       = useState(() => readBool(TOAST_KEY, true));
  const prevCountRef  = useRef(null);
  const seenSessRef   = useRef(new Set());
  const baseTitleRef  = useRef(typeof document !== 'undefined' ? document.title : '');
  const wrapRef       = useRef(null);

  // Persist prefs
  useEffect(() => { localStorage.setItem(CHIME_KEY, chimeOn ? '1' : '0'); }, [chimeOn]);
  useEffect(() => { localStorage.setItem(TOAST_KEY, toastOn ? '1' : '0'); }, [toastOn]);

  const openSession = useCallback((sessionId) => {
    setOpen(false);
    if (typeof onNavigateToChats === 'function') onNavigateToChats(sessionId);
  }, [onNavigateToChats]);

  // Poll unread + sessions
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const [c, s] = await Promise.all([
          axios.get(`${API}/chat/unread-count`),
          axios.get(`${API}/chat/sessions`),
        ]);
        if (cancelled) return;
        const nextCount = c.data?.unread_count || 0;
        const nextSessions = (s.data?.sessions || []).filter((x) => !x.closed_at);

        // "Every unread message" cue: any INCREASE fires chime + toast
        const prev = prevCountRef.current;
        if (prev !== null && nextCount > prev) {
          if (chimeOn) playChatChime();
          if (toastOn) {
            // Find the freshest session that is unread (not previously seen or newer)
            const fresh = nextSessions.find(
              (x) => x.unread_for_dispatcher && !seenSessRef.current.has(x.id + '::' + x.last_message_at),
            );
            const label = fresh
              ? (fresh.load_ref ? `New message on load #${fresh.load_ref}` : 'New carrier message')
              : `${nextCount - prev} new message${nextCount - prev > 1 ? 's' : ''}`;
            toast(label, {
              description: fresh?.last_preview || 'A carrier just sent a message',
              action: fresh ? { label: 'Open', onClick: () => openSession(fresh.id) } : undefined,
              duration: 6000,
            });
          }
        }
        // Track seen (session id + last_message_at fingerprint)
        seenSessRef.current = new Set(nextSessions.map((x) => x.id + '::' + x.last_message_at));

        prevCountRef.current = nextCount;
        setCount(nextCount);
        setSessions(nextSessions);
      } catch {
        // Non-fatal — keep polling
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [chimeOn, toastOn, openSession]);

  // Tab title flash while there are unreads
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (count > 0) {
      document.title = `(${count}) ${baseTitleRef.current}`;
    } else {
      document.title = baseTitleRef.current;
    }
  }, [count]);

  // Click-outside to close
  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setShowPrefs(false);
      }
    };
    if (open) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative bg-zinc-800 hover:bg-zinc-700 text-white p-2.5 transition-colors"
        data-testid="chat-bell-btn"
        title={count > 0 ? `${count} unread carrier message${count > 1 ? 's' : ''}` : 'No new messages'}
      >
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-black flex items-center justify-center rounded-full ring-2 ring-zinc-900"
            data-testid="chat-bell-badge"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 bg-white border border-zinc-200 shadow-xl z-50"
          data-testid="chat-bell-dropdown"
        >
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div className="text-xs font-black uppercase tracking-wider text-zinc-900">
              Carrier Chats · <span className="text-red-600">{count}</span> unread
            </div>
            <button
              onClick={() => setShowPrefs((v) => !v)}
              className="text-zinc-400 hover:text-zinc-900 text-xs font-bold"
              data-testid="chat-bell-prefs-btn"
              title="Notification preferences"
            >
              ⚙
            </button>
          </div>

          {showPrefs && (
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 space-y-2" data-testid="chat-bell-prefs">
              <PrefRow
                icon={chimeOn ? Volume2 : VolumeX}
                label="Chime on new message"
                on={chimeOn}
                onToggle={() => setChimeOn((v) => !v)}
                testId="chat-bell-toggle-chime"
              />
              <PrefRow
                icon={toastOn ? Bell : BellOff}
                label="Toast popup"
                on={toastOn}
                onToggle={() => setToastOn((v) => !v)}
                testId="chat-bell-toggle-toast"
              />
            </div>
          )}

          <div className="max-h-96 overflow-y-auto">
            {sessions.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-zinc-500" data-testid="chat-bell-empty">
                <MessageCircle className="w-6 h-6 mx-auto mb-2 text-zinc-300" />
                No open chats
              </div>
            ) : (
              sessions.slice(0, 6).map((s) => (
                <button
                  key={s.id}
                  onClick={() => openSession(s.id)}
                  className={`w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${
                    s.unread_for_dispatcher ? 'bg-red-50/50' : ''
                  }`}
                  data-testid={`chat-bell-session-${s.id}`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="text-xs font-black text-zinc-900 truncate">
                      {s.carrier_name || 'Anonymous carrier'}
                    </div>
                    {s.unread_for_dispatcher && (
                      <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {s.load_ref ? <span className="font-bold text-zinc-700">Load #{s.load_ref} · </span> : null}
                    {s.last_preview || '—'}
                  </div>
                  <div className="text-[10px] text-zinc-400 mt-0.5">
                    {s.last_message_at ? new Date(s.last_message_at).toLocaleString() : ''}
                  </div>
                </button>
              ))
            )}
          </div>

          {sessions.length > 0 && (
            <button
              onClick={() => openSession(null)}
              className="w-full py-2.5 bg-zinc-900 hover:bg-red-600 text-white text-[11px] font-black uppercase tracking-wider transition-colors"
              data-testid="chat-bell-view-all"
            >
              View all in Live Chats →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const PrefRow = ({ icon: Icon, label, on, onToggle, testId }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2 text-xs text-zinc-700">
      <Icon className={`w-3.5 h-3.5 ${on ? 'text-zinc-900' : 'text-zinc-400'}`} />
      {label}
    </div>
    <button
      onClick={onToggle}
      data-testid={testId}
      className={`relative w-9 h-5 transition-colors ${on ? 'bg-red-600' : 'bg-zinc-300'}`}
    >
      <span className={`absolute top-0.5 w-4 h-4 bg-white transition-all ${on ? 'left-4' : 'left-0.5'}`} />
    </button>
  </div>
);
